/**
 * The EFFECTIVE dictionary — built in, with every Admin edit applied (ADR
 * central-data-model). An override holds only what was changed; a `null`
 * attribute follows the built-in, so a better built-in description still
 * reaches every field nobody edited. A code override with no built-in is the
 * whole description of a code only the data knows.
 */
import {
  builtInCodes,
  builtInFields,
  codedFieldIds,
  codeId,
  ENTITY_INFO,
  fieldId,
  splitCodeId,
} from './builtins.ts'
import {
  type CodeOverride,
  DATA_ENTITIES,
  type DataDictionaryView,
  type DataEntity,
  type EffectiveCode,
  type EffectiveField,
  type FieldOverride,
} from './types.ts'

export function effectiveFields(overrides: readonly FieldOverride[]): EffectiveField[] {
  const byId = new Map(overrides.map((o) => [o.id, o]))
  return builtInFields.map((f) => {
    const id = fieldId(f.entity, f.key)
    const o = byId.get(id)
    const edited = !!o && (o.label !== null || o.description !== null)
    return {
      id,
      entity: f.entity,
      key: f.key,
      label: o?.label ?? f.label,
      description: o?.description ?? f.description,
      kind: f.kind,
      unit: f.unit ?? null,
      tag: f.tag,
      builtIn: { label: f.label, description: f.description },
      edited,
      updatedAt: edited ? (o?.updatedAt ?? null) : null,
      updatedBy: edited ? (o?.updatedBy ?? null) : null,
    }
  })
}

const isEdited = (o: CodeOverride) =>
  o.label !== null || o.meaning !== null || o.confidence !== null || o.evidence !== null

export function effectiveCodes(overrides: readonly CodeOverride[]): EffectiveCode[] {
  const byId = new Map(overrides.map((o) => [o.id, o]))
  const seen = new Set<string>()
  const merged: EffectiveCode[] = builtInCodes.map((c) => {
    const field = fieldId(c.entity, c.field)
    const id = codeId(field, c.value)
    seen.add(id)
    const o = byId.get(id)
    const edited = !!o && isEdited(o)
    return {
      id,
      fieldId: field,
      value: c.value,
      label: o?.label ?? c.label,
      meaning: o?.meaning ?? c.meaning,
      confidence: o?.confidence ?? c.confidence,
      evidence: o?.evidence ?? c.evidence ?? null,
      builtIn: {
        label: c.label,
        meaning: c.meaning,
        confidence: c.confidence,
        evidence: c.evidence ?? null,
      },
      edited,
      updatedAt: edited ? (o?.updatedAt ?? null) : null,
      updatedBy: edited ? (o?.updatedBy ?? null) : null,
    }
  })
  for (const o of overrides) {
    if (seen.has(o.id) || !isEdited(o)) continue
    const parts = splitCodeId(o.id)
    if (!parts || !codedFieldIds.includes(parts.fieldId)) continue
    merged.push({
      id: o.id,
      fieldId: parts.fieldId,
      value: parts.value,
      label: o.label,
      meaning: o.meaning,
      confidence: o.confidence,
      evidence: o.evidence,
      builtIn: null,
      edited: true,
      updatedAt: o.updatedAt,
      updatedBy: o.updatedBy,
    })
  }
  return merged
}

export function buildDictionary(
  fieldOverrides: readonly FieldOverride[],
  codeOverrides: readonly CodeOverride[],
): DataDictionaryView {
  return {
    entities: DATA_ENTITIES.map((entity) => ({ entity, ...ENTITY_INFO[entity] })),
    fields: effectiveFields(fieldOverrides),
    codes: effectiveCodes(codeOverrides),
    codedFields: [...codedFieldIds],
  }
}

/**
 * Fast lookups over one dictionary — what screens and server code read.
 * Falls back to the key itself, never throws, so a field added later still
 * renders.
 */
export interface DictionaryLookup {
  field(entity: DataEntity, key: string): EffectiveField | undefined
  label(entity: DataEntity, key: string, fallback?: string): string
  code(fieldIdValue: string, value: string): EffectiveCode | undefined
  /** A code's short name, or the value itself when it has none. */
  codeLabel(fieldIdValue: string, value: string): string
  /** "on_hold — On hold", or the value alone when it has no label. */
  codeText(fieldIdValue: string, value: string): string
  /** Every described code of one field, as stored. */
  codesFor(fieldIdValue: string): string[]
}

export function lookup(dictionary: {
  fields: readonly EffectiveField[]
  codes: readonly EffectiveCode[]
}): DictionaryLookup {
  const fields = new Map(dictionary.fields.map((f) => [f.id, f]))
  const codes = new Map(dictionary.codes.map((c) => [c.id, c]))
  return {
    field: (entity, key) => fields.get(fieldId(entity, key)),
    label: (entity, key, fallback) => fields.get(fieldId(entity, key))?.label ?? fallback ?? key,
    code: (field, value) => codes.get(codeId(field, value)),
    codeLabel: (field, value) => codes.get(codeId(field, value))?.label ?? value,
    codesFor: (field) => dictionary.codes.filter((c) => c.fieldId === field).map((c) => c.value),
    codeText(field, value) {
      const label = codes.get(codeId(field, value))?.label
      return label && label !== value ? `${value} — ${label}` : value
    },
  }
}

let builtIn: { dictionary: DataDictionaryView; lookup: DictionaryLookup } | undefined
/**
 * The built-in dictionary — what screens show before the server's answer
 * arrives. Built on first use, never at import, so a module the dictionary's
 * content reads (src/core/items) can one day read a label back without an
 * import cycle (docs/DATA-MODEL.md → "Import cycles").
 */
export function builtInDictionary(): DataDictionaryView {
  builtIn ??= (() => {
    const dictionary = buildDictionary([], [])
    return { dictionary, lookup: lookup(dictionary) }
  })()
  return builtIn.dictionary
}
export function builtInLookup(): DictionaryLookup {
  builtInDictionary()
  return (builtIn as { lookup: DictionaryLookup }).lookup
}
